// uiarc: skeleton (https://uiarc.dev/r/skeleton.json).
// - `Skeleton`: tek blok (mevcut API). uiarc'ın sakin, sonlu nabzı: her blok üstündekinden bir vuruş sonra solar
//   (`style={{ '--index': n }}` ile 90ms'lik kademe), yaklaşık 20 sn sonra durur. `prefers-reduced-motion` altında nabız yok.
// - `SkeletonPlaceholder`: uiarc'ın kendi bileşeni (satırlar + isteğe bağlı avatar); `children` verilirse yükleme
//   bitince yer tutucu içeriğe çapraz geçer ve yükseklik yayla yeni boya uyar.
import {
  useEffect,
  useLayoutEffect,
  useRef,
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { AnimatePresence, animate, motion, useMotionValue, useReducedMotion } from 'motion/react'
import type { AnimationPlaybackControls } from 'motion/react'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

const pulse =
  'animate-[skeleton-pulse_1.8s_var(--ease-in-out)_calc(var(--index,0)*90ms)_11_both] motion-reduce:animate-none'

function Skeleton({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="skeleton"
      className={cn(
        'rounded-control block flex-none bg-[color-mix(in_oklab,var(--border-strong)_38%,var(--surface))]',
        pulse,
        className,
      )}
      {...props}
    />
  )
}

/** İçerik yüksekliğini izler; `busy` değişince eski boydan yeni boya yayla gider, sonra auto'ya döner. Pasif yeniden akışlar anında izler. */
function HeightFrame({
  className,
  busy,
  reduce,
  children,
}: {
  className?: string
  busy: boolean
  reduce: boolean | null
  children: ReactNode
}) {
  const frame = useRef<HTMLDivElement>(null)
  const content = useRef<HTMLDivElement>(null)
  const height = useMotionValue<number | 'auto'>('auto')
  const changedAt = useRef(0)
  useLayoutEffect(() => {
    changedAt.current = performance.now()
  }, [busy])
  useEffect(() => {
    const node = content.current
    if (!node || typeof ResizeObserver === 'undefined') return
    let last: number | undefined
    let controls: AnimationPlaybackControls | undefined
    const settle = () => {
      height.jump('auto')
      if (frame.current) Object.assign(frame.current.style, { overflow: '', height: 'auto' })
    }
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      const next = entry.borderBoxSize?.[0]?.blockSize ?? node.offsetHeight
      const current = height.get()
      const from = typeof current === 'number' ? current : last
      last = next
      controls?.stop()
      if (
        reduce ||
        from === undefined ||
        from === next ||
        performance.now() - changedAt.current > 120
      )
        return settle()
      // Eski yüksekliği bu kare boyanmadan sabitle, sonra yenisine yayla git.
      if (frame.current)
        Object.assign(frame.current.style, { overflow: 'hidden', height: `${from}px` })
      controls = animate(height, [from, next], {
        ...motionTokens.spring.smooth,
        onComplete: settle,
      })
    })
    observer.observe(node)
    return () => {
      observer.disconnect()
      controls?.stop()
    }
  }, [height, reduce])
  return (
    <motion.div ref={frame} className={className} aria-busy={busy} style={{ height }}>
      <div ref={content} className="relative flow-root">
        {children}
      </div>
    </motion.div>
  )
}

interface SkeletonPlaceholderProps {
  label?: string
  lines?: number
  avatar?: boolean
  className?: string
  /** Yükleme bitince gösterilecek içerik; verilirse yer tutucu içeriğe çapraz geçer. */
  children?: ReactNode
  /** Doğruyken yer tutucuyu tutar. Yalnız `children` ile kullanılır. */
  loading?: boolean
}

function SkeletonPlaceholder({
  label = 'Yükleniyor',
  lines = 3,
  avatar = false,
  className,
  children,
  loading = true,
}: SkeletonPlaceholderProps) {
  const reduce = useReducedMotion()
  const count = Math.min(Math.max(Math.floor(lines), 1), 6)
  const { duration, ease } = motionTokens
  // Her blok üstündekinden bir vuruş sonra solar; bekleyiş tek sakin bir dalga gibi okunur.
  const placeholder = (extra?: string) => (
    <div
      data-slot="skeleton-placeholder"
      className={cn('flex w-full min-w-0 items-start gap-4', extra)}
      role="status"
      aria-label={label}
      aria-busy="true"
    >
      {avatar && (
        <Skeleton
          className="rounded-pill size-(--control-height-md)"
          style={{ '--index': 0 } as CSSProperties}
          aria-hidden="true"
        />
      )}
      <span className="grid flex-1 gap-3 pt-1" aria-hidden="true">
        {Array.from({ length: count }, (_, index) => (
          <Skeleton
            key={index}
            className={cn(
              'h-3 w-full',
              index === 0 && 'h-4 w-[48%]',
              index === count - 1 && 'w-[72%]',
            )}
            style={{ '--index': index + (avatar ? 1 : 0) } as CSSProperties}
          />
        ))}
      </span>
    </div>
  )
  if (children === undefined) return placeholder(className)
  const instant = { duration: duration.instant }
  return (
    <HeightFrame className={className} busy={loading} reduce={reduce}>
      <AnimatePresence mode="popLayout" initial={false}>
        {loading ? (
          <motion.div
            key="placeholder"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: reduce ? duration.instant : duration.fast,
              ease: ease.standard,
            }}
            exit={{
              opacity: 0,
              transition: { duration: reduce ? duration.instant : duration.fast },
            }}
          >
            {placeholder()}
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, transition: reduce ? instant : { duration: duration.fast } }}
            transition={{
              duration: reduce ? duration.instant : duration.standard,
              ease: ease.enter,
            }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </HeightFrame>
  )
}

export { Skeleton, SkeletonPlaceholder, type SkeletonPlaceholderProps }
